import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditableField, EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import { ICreateIdentityProviderInput, IIdentityProviderOutput, IListCustomerItem, IOidcDiscoveryStrategyOptions, IOidcManualStrategyOptions, ISamlDiscoveryStrategyOptions, ISamlManualStrategyOptions, IUpdateIdentityProviderInput, IdentityProviderStrategy, RequestStatus, api } from "../../../logic/api";
import { EditableGroup, EditableGroupContext, EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { SessionContext } from "../../../utility/useSession";
import { IDropDownListItem } from "../../../components/editable/EditableField/fields/DropDownField";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { formatCamel } from "../../../utility/utils";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";

import cssClasses from "./IdentityProviders.module.css";

const NO_PAGING_LIMIT = -146763;

interface ITenantOption {
  uuid: string;
  label: string;
}

interface IPrivateKeyJwtFormFields {
  kid?: string;
  signingAlgorithm?: string;
  x5tS256?: string;
}

interface IStrategyOptionsFormFields {
  discoveryEndpoint?: string;
  clientId?: string;
  clientAuthenticationMethod?: string;
  clientSecret?: string;
  privateKeyPem?: string;
  privateKeyJwt?: IPrivateKeyJwtFormFields;
  authoriseEndpoint?: string;
  tokenEndpoint?: string;
  jwksUri?: string;
  certificates?: string;
  loginUrl?: string;
}

interface IFormFields {
  id: number | null;
  uuid: string | null;
  name: string;
  issuer: string;
  strategy: IdentityProviderStrategy;
  configType: string;
  privateKeyJwtConfigured: boolean;
  strategyOptions: IStrategyOptionsFormFields;
  domains: string[];
  tenants: string[];
  defaults: {
    userActive: boolean;
  },
}

const ClientAuthenticationMethods = {
  clientSecret: "client_secret",
  privateKeyJwt: "private_key_jwt",
};

export interface IdentityProviderFormProps {
  open: boolean;
  identityProviderId: number | null;
  onClose: () => void;
  onSave: (identityProvider: IIdentityProviderOutput) => void;
}

export const IdentityProviderForm: FC<IdentityProviderFormProps> = ({
  open,
  identityProviderId,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const isTenantPickerReleased = useReleaseManagement(ReleaseFlag.releaseIdentityProviderTenantPicker);

  const [errors, setErrors] = useState<string[] | null>(null);
  const [formData, setFormData] = useState<IFormFields | null>(null);
  const [tenantOptions, setTenantOptions] = useState<ITenantOption[]>([]);

  const loadIdP = api.hooks.admin.getIdentityProviderById();
  const createIdP = api.hooks.admin.createIdentityProvider();
  const updateIdP = api.hooks.admin.updateIdentityProvider();
  const listCustomers = api.hooks.admin.listCustomers();

  const handleClose = () => {
    setErrors(null);
    onClose();
  }

  const handleSave = () => {
    if (!formData) {
      return;
    }

    setErrors(null);
    const errors = validateFormFields(formData);
    if (errors.length) {
      setErrors(errors);
      return;
    }

    const handleResponse = (result: IIdentityProviderOutput | null) => {
      if (!result) {
        setErrors([t("admin.identityProviders.form.errors.saveFailed")]);
        return;
      }

      onSave(result);
    }

    if (formData.id) {
      updateIdP.send(
        session.current.context,
        mapFromFormFields(formData) as IUpdateIdentityProviderInput,
        handleResponse,
      );
    } else {
      createIdP.send(
        session.current.context,
        mapFromFormFields(formData) as ICreateIdentityProviderInput,
        handleResponse
      );
    }
  }

  useEffect(() => {
    if (!open || !identityProviderId) {
      setFormData({
        id: null,
        uuid: null,
        name: "",
        issuer: "",
        strategy: IdentityProviderStrategy.OIDC_v1,
        configType: "discovery",
        privateKeyJwtConfigured: false,
        strategyOptions: {
          clientAuthenticationMethod: ClientAuthenticationMethods.clientSecret,
        },
        domains: [],
        tenants: [],
        defaults: {
          userActive: true,
        },
      });
      return;
    }

    loadIdP.send(
      session.current.context,
      { id: identityProviderId },
      (result) => {
        if (!result) {
          setErrors([t("admin.identityProviders.form.errors.notFound")]);
          return;
        }

        setFormData(mapToFormFields(result));
      }
    );
  }, [identityProviderId]);

  useEffect(() => {
    if (!open || !isTenantPickerReleased) {
      return;
    }

    listCustomers.send(
      session.current.context,
      { paging: { limit: NO_PAGING_LIMIT, offset: 0 } },
      (result) => {
        setTenantOptions(mapCustomerOptions(result || []));
      },
    );
  }, [open, isTenantPickerReleased]);

  return <Dialog open={open} fullWidth maxWidth="md" className={cssClasses["idp-form"]}>
    <DialogTitle>
      <div style={{ textAlign: "center" }}>
        {t("admin.identityProviders.form.title")}
      </div>
    </DialogTitle>

    <DialogContent>
      <ErrorDialog err={errors} />

      {!formData && (
        <CircularProgress />
      )}

      {formData && (
        <EditableGroup<IFormFields>
          id="identity-provider-form"
          testId="identity-provider-form-group"
          editType={EditableGroupType.alwaysEditing}
          data={formData}
          onDataChanged={async (data) => {
            const newData = { ...data };
            setFormData(newData);
            return true;
          }}
        >
          <TabbedView animated={false}>
            <TabbedViewPage title="General">
              <EditableField<IFormFields, string>
                inputType={EditableFieldTypes.text}
                label={t("admin.identityProviders.form.labels.name")}
                useFullWidthWhenEditing
                maxWidth={500}
                onProvide={(data) => data.name}
                onChange={(data, value) => data.name = value}
              />

              <EditableField<IFormFields, string>
                inputType={EditableFieldTypes.text}
                label={t("admin.identityProviders.form.labels.issuer")}
                useFullWidthWhenEditing
                maxWidth={500}
                onProvide={(data) => data.issuer}
                onChange={(data, value) => data.issuer = value}
              />
            </TabbedViewPage>

            <TabbedViewPage title="Strategy">
              <StrategyFields />
            </TabbedViewPage>

            <TabbedViewPage title="Configuration">
              <EditableField<IFormFields, string>
                inputType={EditableFieldTypes.textMultiLineFull}
                label={t("admin.identityProviders.form.labels.domains")}
                useFullWidthWhenEditing
                onProvide={(data) => (data.domains || []).join("\n")}
                onChange={(data, value) => data.domains = (value || "").split("\n").map((v) => v.trim())}
              />

              {!isTenantPickerReleased && (
                <EditableField<IFormFields, string>
                  inputType={EditableFieldTypes.textMultiLineFull}
                  label={t("admin.identityProviders.form.labels.tenants")}
                  useFullWidthWhenEditing
                  onProvide={(data) => (data.tenants || []).join("\n")}
                  onChange={(data, value) => data.tenants = (value || "").split("\n").map((v) => v.trim())}
                />
              )}

              {isTenantPickerReleased && formData && (
                <TenantMultiSelect
                  label={t("admin.identityProviders.form.labels.tenants")}
                  loading={listCustomers.status === RequestStatus.loading}
                  loadingText={t("admin.identityProviders.form.loading.tenants")}
                  noOptionsText={t("admin.noCustomers")}
                  options={tenantOptions}
                  placeholder={t("admin.identityProviders.form.placeholders.searchTenants")}
                  value={mapSelectedTenantOptions(formData.tenants, tenantOptions)}
                  onChange={(value) => {
                    setFormData({
                      ...formData,
                      tenants: value.map((item) => item.uuid).filter(Boolean),
                    });
                  }}
                />
              )}

              <EditableField<IFormFields, boolean>
                inputType={EditableFieldTypes.switch}
                label={t("admin.identityProviders.form.labels.createUsersAsActive")}
                useFullWidthWhenEditing
                onProvide={(data) => data.defaults.userActive}
                onChange={(data, value) => data.defaults.userActive = value}
              />
            </TabbedViewPage>
          </TabbedView>
        </EditableGroup>
      )}
    </DialogContent>

    <DialogActions>
      <Button onClick={handleClose} color="primary">
        {t("general.close")}
      </Button>

      <Button onClick={handleSave} color="primary">
        {t("general.save")}
      </Button>
    </DialogActions>
  </Dialog>;
};

const TenantMultiSelect: FC<{
  label: string;
  loading: boolean;
  loadingText: string;
  noOptionsText: string;
  options: ITenantOption[];
  placeholder: string;
  value: ITenantOption[];
  onChange: (value: ITenantOption[]) => void;
}> = ({
  label,
  loading,
  loadingText,
  noOptionsText,
  options,
  placeholder,
  value,
  onChange,
}) => {
    return (
      <Box sx={{ paddingTop: 2, paddingLeft: 1, paddingRight: 1 }}>
        <Autocomplete
          multiple
          fullWidth
          openOnFocus
          limitTags={3}
          options={options}
          value={value}
          loading={loading}
          loadingText={loadingText}
          noOptionsText={noOptionsText}
          disableCloseOnSelect
          isOptionEqualToValue={(option, selectedValue) => option.uuid === selectedValue.uuid}
          getOptionLabel={(option) => option.label}
          filterOptions={(filterOptions, state) => {
            const query = state.inputValue.trim().toLowerCase();

            if (!query) {
              return filterOptions;
            }

            return filterOptions.filter((option) => (
              option.label.toLowerCase().includes(query)
              || option.uuid.toLowerCase().includes(query)
            ));
          }}
          onChange={(_event, newValue) => {
            onChange(newValue);
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

            return (
              <li key={key} {...optionProps}>
                <SelectionIcon
                  aria-hidden="true"
                  fontSize="small"
                  sx={{
                    color: selected ? "brand.main" : "action.disabled",
                    flexShrink: 0,
                    mr: 1,
                  }}
                />
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => {
            const { InputLabelProps, InputProps, inputProps, ...textFieldParams } = params;

            return (
              <TextField
                {...textFieldParams}
                label={label}
                placeholder={placeholder}
                fullWidth
                slotProps={{
                  inputLabel: InputLabelProps,
                  htmlInput: inputProps,
                  input: {
                    ...InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={18} /> : null}
                        {InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            );
          }}
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              alignContent: "flex-start",
              paddingBottom: "8px",
              paddingTop: "8px",
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "calc(100% - 8px)",
            },
            "& .MuiAutocomplete-input": {
              flexBasis: "100%",
              minWidth: "100% !important",
            },
          }}
        />
      </Box>
    );
  };

const StrategyFields: FC = () => {
  const { t } = useTranslation();
  const editableGroup = useContext(EditableGroupContext);
  const formData = editableGroup.data as IFormFields;

  return <>
    <EditableField<IFormFields, IDropDownListItem>
      inputType={EditableFieldTypes.enumDropDownField}
      label={t("admin.identityProviders.form.labels.strategy")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => ({ name: "", value: String(data.strategy || -1) })}
      onRequestText={(data) => data.strategy ? formatCamel(IdentityProviderStrategy[data.strategy]) : ""}
      onChange={(data, value) => data.strategy = parseInt(value.value || "")}
      onRequestData={async () => {
        const items: IDropDownListItem[] = [
          {
            name: "Please select",
            value: "-1",
          }
        ];
        for (const k in IdentityProviderStrategy) {
          if (!isNaN(parseInt(k)) || k === "local") {
            continue;
          }

          items.push({
            name: k.toString(),
            value: IdentityProviderStrategy[k],
          });
        }
        return items;
      }}
    />

    <EditableField<IFormFields, IDropDownListItem>
      inputType={EditableFieldTypes.enumDropDownField}
      label={t("admin.identityProviders.form.labels.strategyConfigType")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => ({ name: data.configType, value: data.configType })}
      onChange={(data, value) => data.configType = value.value || ""}
      onRequestData={async () => ([
        { name: "Discovery", value: "discovery" },
        { name: "Manual", value: "manual" },
      ])}
    />

    {(formData.strategy === IdentityProviderStrategy.OIDC_v1 && formData.configType === "discovery") && (
      <OidcDiscoveryFields />
    )}

    {(formData.strategy === IdentityProviderStrategy.OIDC_v1 && formData.configType === "manual") && (
      <OidcManualFields />
    )}

    {(formData.strategy === IdentityProviderStrategy.SAML_v2 && formData.configType === "discovery") && (
      <SamlDiscoveryFields />
    )}

    {(formData.strategy === IdentityProviderStrategy.SAML_v2 && formData.configType === "manual") && (
      <SamlManualFields />
    )}
  </>
};

const OidcPrivateKeyJwtFields: FC = () => {
  const { t } = useTranslation();

  return <>
    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.signingAlgorithm")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.privateKeyJwt?.signingAlgorithm}
      onChange={(data, value) => data.strategyOptions.privateKeyJwt = { ...(data.strategyOptions.privateKeyJwt || {}), signingAlgorithm: value }}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.kid")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.privateKeyJwt?.kid}
      onChange={(data, value) => data.strategyOptions.privateKeyJwt = { ...(data.strategyOptions.privateKeyJwt || {}), kid: value }}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.x5tS256")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.privateKeyJwt?.x5tS256}
      onChange={(data, value) => data.strategyOptions.privateKeyJwt = { ...(data.strategyOptions.privateKeyJwt || {}), x5tS256: value }}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.textMultiLine}
      label={t("admin.identityProviders.form.labels.clientPem")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.privateKeyPem}
      onChange={(data, value) => data.strategyOptions.privateKeyPem = value}
    />
  </>
};

const OidcClientAuthenticationFields: FC = () => {
  const { t } = useTranslation();
  const editableGroup = useContext(EditableGroupContext);
  const formData = editableGroup.data as IFormFields;
  const clientAuthenticationMethod =
    formData.strategyOptions.clientAuthenticationMethod || ClientAuthenticationMethods.clientSecret;

  return <>
    <EditableField<IFormFields, IDropDownListItem>
      inputType={EditableFieldTypes.enumDropDownField}
      label={t("admin.identityProviders.form.labels.clientAuthenticationMethod")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => ({
        name: "",
        value: data.strategyOptions.clientAuthenticationMethod || ClientAuthenticationMethods.clientSecret,
      })}
      onChange={(data, value) => data.strategyOptions.clientAuthenticationMethod = value.value}
      onRequestData={async () => ([
        {
          name: t("admin.identityProviders.form.clientAuthenticationMethods.clientSecret"),
          value: ClientAuthenticationMethods.clientSecret,
        },
        {
          name: t("admin.identityProviders.form.clientAuthenticationMethods.privateKeyJwt"),
          value: ClientAuthenticationMethods.privateKeyJwt,
        },
      ])}
    />

    {clientAuthenticationMethod === ClientAuthenticationMethods.clientSecret && (
      <EditableField<IFormFields, string | undefined>
        inputType={EditableFieldTypes.text}
        label={t("admin.identityProviders.form.labels.clientSecret")}
        useFullWidthWhenEditing
        maxWidth={500}
        onProvide={(data) => data.strategyOptions.clientSecret}
        onChange={(data, value) => data.strategyOptions.clientSecret = value}
      />
    )}

    {clientAuthenticationMethod === ClientAuthenticationMethods.privateKeyJwt && (
      <OidcPrivateKeyJwtFields />
    )}
  </>
};

const OidcDiscoveryFields: FC = () => {
  const { t } = useTranslation();

  return <>
    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.discoveryEndpoint")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.discoveryEndpoint}
      onChange={(data, value) => data.strategyOptions.discoveryEndpoint = value}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.clientId")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.clientId}
      onChange={(data, value) => data.strategyOptions.clientId = value}
    />

    <OidcClientAuthenticationFields />
  </>
}

const OidcManualFields: FC = () => {
  const { t } = useTranslation();

  return <>
    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.clientId")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.clientId}
      onChange={(data, value) => data.strategyOptions.clientId = value}
    />

    <OidcClientAuthenticationFields />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.authoriseEndpoint")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.authoriseEndpoint}
      onChange={(data, value) => data.strategyOptions.authoriseEndpoint = value}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.jwksUri")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.jwksUri}
      onChange={(data, value) => data.strategyOptions.jwksUri = value}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.tokenEndpoint")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.tokenEndpoint}
      onChange={(data, value) => data.strategyOptions.tokenEndpoint = value}
    />
  </>
}

const SamlDiscoveryFields: FC = () => {
  const { t } = useTranslation();

  return <>
    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.discoveryEndpoint")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.discoveryEndpoint}
      onChange={(data, value) => data.strategyOptions.discoveryEndpoint = value}
    />
  </>
}

const SamlManualFields: FC = () => {
  const { t } = useTranslation();

  return <>
    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.text}
      label={t("admin.identityProviders.form.labels.loginUrl")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.loginUrl}
      onChange={(data, value) => data.strategyOptions.loginUrl = value}
    />

    <EditableField<IFormFields, string | undefined>
      inputType={EditableFieldTypes.textMultiLineFull}
      label={t("admin.identityProviders.form.labels.certificates")}
      useFullWidthWhenEditing
      maxWidth={500}
      onProvide={(data) => data.strategyOptions.certificates}
      onChange={(data, value) => data.strategyOptions.certificates = value}
    />
  </>
}

const mapCustomerOptions = (customers: IListCustomerItem[]): ITenantOption[] => {
  return customers.map((customer) => ({
    uuid: customer.uuid,
    label: customer.clientCode ? `${customer.name} (${customer.clientCode})` : customer.name,
  }));
};

const mapSelectedTenantOptions = (tenants: string[], options: ITenantOption[]): ITenantOption[] => {
  return (tenants || [])
    .filter((tenant) => !!tenant)
    .map((tenant) => options.find((option) => option.uuid === tenant) || {
      uuid: tenant,
      label: tenant,
    });
};

const mapToFormFields = (identityProvider: IIdentityProviderOutput): IFormFields => {
  const strategyOptions = { ...(identityProvider.strategyOptions || {}) } as unknown as IStrategyOptionsFormFields & { certificates?: string | string[] };
  if (Array.isArray(strategyOptions.certificates)) {
    strategyOptions.certificates = strategyOptions.certificates.join("\n");
  }

  const configType = strategyOptions.discoveryEndpoint ? "discovery" : "manual";
  const clientAuthenticationMethod = strategyOptions.clientAuthenticationMethod ||
    (strategyOptions.privateKeyJwt
      ? ClientAuthenticationMethods.privateKeyJwt
      : ClientAuthenticationMethods.clientSecret);
  const privateKeyJwtConfigured = clientAuthenticationMethod === ClientAuthenticationMethods.privateKeyJwt &&
    Boolean(strategyOptions.privateKeyJwt);

  return {
    id: identityProvider.id,
    uuid: identityProvider.uuid,
    configType: configType,
    privateKeyJwtConfigured,
    domains: identityProvider.domains,
    issuer: identityProvider.issuer,
    name: identityProvider.name,
    strategy: identityProvider.strategy,
    strategyOptions: {
      ...strategyOptions,
      clientAuthenticationMethod,
    } as IStrategyOptionsFormFields,
    tenants: identityProvider.tenants,
    defaults: {
      userActive: identityProvider.defaults.userActive,
    },
  }
};

const mapFromFormFields = (fields: IFormFields): ICreateIdentityProviderInput | IUpdateIdentityProviderInput => {
  const clientAuthenticationMethod = fields.strategyOptions.clientAuthenticationMethod || ClientAuthenticationMethods.clientSecret;
  const usesPrivateKeyJwt = clientAuthenticationMethod === ClientAuthenticationMethods.privateKeyJwt;
  const privateKeyPem = fields.strategyOptions.privateKeyPem?.trim() || undefined;

  const mapPrivateKeyJwt = () => {
    if (!usesPrivateKeyJwt) {
      return undefined;
    }

    const privateKeyJwt = fields.strategyOptions.privateKeyJwt;
    if (!privateKeyJwt) {
      return undefined;
    }

    const result = {
      kid: privateKeyJwt.kid?.trim(),
      signingAlgorithm: privateKeyJwt.signingAlgorithm?.trim(),
      x5tS256: privateKeyJwt.x5tS256?.trim(),
    };

    return result.kid || result.signingAlgorithm || result.x5tS256 ? result : undefined;
  };

  const mapOidcDiscovery = (): IOidcDiscoveryStrategyOptions => ({
    discoveryEndpoint: fields.strategyOptions.discoveryEndpoint || "",
    clientId: fields.strategyOptions.clientId || "",
    clientAuthenticationMethod,
    clientSecret: usesPrivateKeyJwt ? undefined : fields.strategyOptions.clientSecret?.trim(),
    privateKeyPem: usesPrivateKeyJwt ? privateKeyPem : undefined,
    privateKeyJwt: mapPrivateKeyJwt(),
  })

  const mapOidcManual = (): IOidcManualStrategyOptions => ({
    authoriseEndpoint: fields.strategyOptions.authoriseEndpoint || "",
    tokenEndpoint: fields.strategyOptions.tokenEndpoint || "",
    clientId: fields.strategyOptions.clientId || "",
    clientAuthenticationMethod,
    clientSecret: usesPrivateKeyJwt ? undefined : fields.strategyOptions.clientSecret?.trim(),
    privateKeyPem: usesPrivateKeyJwt ? privateKeyPem : undefined,
    privateKeyJwt: mapPrivateKeyJwt(),
    jwksUri: fields.strategyOptions.jwksUri || "",
  });

  const mapSamlDiscovery = (): ISamlDiscoveryStrategyOptions => ({
    discoveryEndpoint: fields.strategyOptions.discoveryEndpoint || "",
  });

  const mapSamlManual = (): ISamlManualStrategyOptions => ({
    certificates: (fields.strategyOptions.certificates || "").split("\n"),
    loginUrl: fields.strategyOptions.loginUrl || "",
  });

  const mapStrategyOptions = () => {
    switch (fields.strategy) {
      case IdentityProviderStrategy.OIDC_v1:
        return fields.configType === "discovery"
          ? mapOidcDiscovery()
          : mapOidcManual();
      case IdentityProviderStrategy.SAML_v2:
        return fields.configType === "discovery"
          ? mapSamlDiscovery()
          : mapSamlManual();
      default:
        throw new Error("Strategy not implemented");
    }
  }

  return {
    id: Number(fields.id),
    domains: fields.domains,
    issuer: fields.issuer,
    name: fields.name,
    strategy: fields.strategy as IdentityProviderStrategy.OIDC_v1 | IdentityProviderStrategy.SAML_v2,
    strategyOptions: mapStrategyOptions(),
    tenants: fields.tenants,
    defaults: {
      userActive: fields.defaults.userActive,
    },
  }
};

const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

const validateOidcDiscovery = (fields: IFormFields): string[] => {
  const errors: string[] = [];

  if (!validateUrl(fields.strategyOptions.discoveryEndpoint || "")) {
    errors.push("Discovery endpoint must be a URL");
  }

  if (!fields.strategyOptions.clientId) {
    errors.push("Client ID is required");
  }

  errors.push(...validateOidcClientAuth(fields));

  return errors;
}

const validateOidcManual = (fields: IFormFields): string[] => {
  const errors: string[] = [];

  if (!fields.strategyOptions.clientId) {
    errors.push("Client ID is required");
  }

  errors.push(...validateOidcClientAuth(fields));

  if (!validateUrl(fields.strategyOptions.authoriseEndpoint || "")) {
    errors.push("Authorise endpoint must be a URL");
  }

  if (!validateUrl(fields.strategyOptions.jwksUri || "")) {
    errors.push("JWKS URI must be a URL");
  }

  if (!validateUrl(fields.strategyOptions.tokenEndpoint || "")) {
    errors.push("Token endpoint must be a URL");
  }

  return errors;
}

const validateOidcClientAuth = (fields: IFormFields): string[] => {
  const errors: string[] = [];
  const clientAuthenticationMethod =
    fields.strategyOptions.clientAuthenticationMethod || ClientAuthenticationMethods.clientSecret;

  if (clientAuthenticationMethod === ClientAuthenticationMethods.clientSecret) {
    if (!fields.strategyOptions.clientSecret?.trim()) {
      errors.push("Client secret is required");
    }

    return errors;
  }

  const hasPrivateKeyPem = Boolean(fields.strategyOptions.privateKeyPem?.trim());
  if (!hasPrivateKeyPem && !fields.privateKeyJwtConfigured) {
    errors.push("Private key PEM is required for private key JWT");
  }

  if (!fields.strategyOptions.privateKeyJwt?.kid?.trim()) {
    errors.push("Key ID is required for private key JWT");
  }

  if (!fields.strategyOptions.privateKeyJwt?.signingAlgorithm?.trim()) {
    errors.push("Signing algorithm is required for private key JWT");
  }

  return errors;
}

const validateSamlDiscovery = (fields: IFormFields): string[] => {
  const errors: string[] = [];

  if (!validateUrl(fields.strategyOptions.discoveryEndpoint || "")) {
    errors.push("Discovery endpoint must be a URL");
  }

  return errors;
}

const validateSamlManual = (fields: IFormFields): string[] => {
  const errors: string[] = [];

  if (!validateUrl(fields.strategyOptions.loginUrl || "")) {
    errors.push("Login URL must be a URL");
  }

  const certificates = fields.strategyOptions.certificates || "";
  if (!certificates.length || !certificates[0]) {
    errors.push("At least one certificate is required");
  }

  return errors;
}

const validateFormFields = (fields: IFormFields): string[] => {
  const errors: string[] = [];

  if (!fields.issuer) {
    errors.push("Issuer is required");
  }

  if (!fields.name) {
    errors.push("Name is required");
  }

  if (fields.strategy === IdentityProviderStrategy.OIDC_v1) {
    if (fields.configType === "discovery") {
      errors.push(...validateOidcDiscovery(fields));
    }

    if (fields.configType === "manual") {
      errors.push(...validateOidcManual(fields));
    }
  }

  if (fields.strategy === IdentityProviderStrategy.SAML_v2) {
    if (fields.configType === "discovery") {
      errors.push(...validateSamlDiscovery(fields));
    }

    if (fields.configType === "manual") {
      errors.push(...validateSamlManual(fields));
    }
  }

  if (!fields.domains?.length || !fields.domains[0]) {
    errors.push("At least one domain is required");
  }

  if (!fields?.tenants.length || !fields.tenants[0]) {
    errors.push("At least one tenant is required");
  }

  return errors;
};
